import { FilterList as FilterListIcon, Search } from "@mui/icons-material";
import { Grid, TextField, InputAdornment, IconButton, Tooltip, Autocomplete } from "@mui/material";
import React, { useRef, useState } from "react";
import { titleize } from "inflection";
import { debounce, map } from "lodash/fp";
import { NotesDrawerFiltersType } from "./types";
import { useQuery } from "@apollo/client/react";
import { CompanyTag, GetCompanyTagsDocument, NotesTypeEnum } from "@/graphql/generated/graphql";

interface DrawerFiltersProps {
  filters: NotesDrawerFiltersType;
  onChange: (key: string, value: string | string[]) => void;
}

const normalDebounce = debounce(500);
const mapIds = map("id");
const DrawerFilters: React.FC<DrawerFiltersProps> = ({ filters, onChange }) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [showFilters, setShowFilters] = useState(false);

  const { data: tagsData } = useQuery(GetCompanyTagsDocument);

  return (
    <Grid
      container
      sx={{ px: 2, py: 1 }}
      justifyContent='space-between'
      data-testid="note-filters"
      alignContent='flex-start'
      maxHeight='fit-content'
      flexDirection='row'
      size={12}>
      <Grid size={10}>
        <TextField
          size="small"
          placeholder="Search"
          fullWidth
          defaultValue={filters.search}
          onChange={(e) => {
            debounceFunc.cancel();
            debounceFunc(() => {
              onChange("search", e.target.value);
            });
          }}
          slotProps={{
            input: {
              sx: { fontSize: 14 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }
          }}
        />
      </Grid>
      <Grid size={1}>
        <Tooltip title="Toggle Filters">
          <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      {
        showFilters &&
        <React.Fragment>
          <Autocomplete
            options={Object.keys(NotesTypeEnum)}
            value={filters.noteType || ""}
            onChange={(_, value) => onChange("noteType", value ? value.toLowerCase() : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Related to"
                size="small"
                fullWidth
              />
            )}
            getOptionLabel={(option) => titleize(option)}
            isOptionEqualToValue={(option, value) => option === value}
            clearOnEscape
            disablePortal
            fullWidth
            sx={{ mb: 1, mt: 1 }}
          />
          <Autocomplete
            multiple
            options={(tagsData?.getCompanyTags || [])}
            getOptionLabel={(option) => option ? option.title : ""}
            value={(filters.tagIds ?? []).map((id: string) => tagsData?.getCompanyTags.find(tag => tag.id === id)).filter(Boolean)}
            onChange={(_, newValue: CompanyTag[]) => onChange("tagIds", mapIds(newValue) as string[])}
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder="Select tags" fullWidth />
            )}
            fullWidth
          />
        </React.Fragment>
      }
    </Grid>
  );
}

export default DrawerFilters;
